import { Icon, Tone } from '~/components/ui';
import { useT } from '~/i18n';
import { formatJobWage, type DraftApplication } from '../lib/demo';

/**
 * Una postulación lista, con SU botón.
 *
 * El botón manda ESA postulación al correo del propio candidato — no a un
 * empleador. Es la única forma de que entienda qué está comprando antes de
 * pagar: no una promesa, el correo terminado en su bandeja.
 *
 * No hay contador. Ni "1 de 3", ni "te quedan 2", ni barra. El backend cuenta
 * tres por dentro y avisa cuando se completaron; acá la persona simplemente
 * envía, y lo que descubre al final es el plan, no una cuota agotada.
 */
interface Props {
  draft: DraftApplication;
  /** Ya llegó a su correo: la card lo dice y el botón desaparece. */
  sent: boolean;
  sending: boolean;
  /** El texto coral cuando el envío no salió. La card no se rompe por eso. */
  error?: string;
  /** False con plan activo: ahí el envío real lo hace el sistema. */
  showSend: boolean;
  onSend: () => void;
}

export default function DraftDemoCard({ draft, sent, sending, error, showSend, onSend }: Props) {
  const t = useT('workspace').dashboard.draftCard;
  const wage = formatJobWage(draft.job);
  return (
    <div
      className="p-3 rounded-2xl"
      style={{ background: 'var(--surface-hi)', border: '1px solid var(--hairline)' }}
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[12rem]">
          <p className="font-medium">
            {draft.job?.company} — {draft.job?.title}
          </p>
          <p className="text-sm hush line-clamp-1">{draft.email_subject}</p>
          {draft.job?.location && <p className="text-xs hush">{draft.job.location}</p>}
          {wage && (
            /* El salario es mint porque es un dato real de la oferta, no una
               promesa nuestra. Con su moneda si no es USD (Canadá: CAD). */
            <p className="text-xs text-data font-medium">{wage}</p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {sent ? (
            <Tone tone="good">{t.sent}</Tone>
          ) : showSend ? (
            <button className="btn btn-primary btn-sm gap-2" onClick={onSend} disabled={sending}>
              {sending ? (
                <span className="loading loading-spinner loading-xs" />
              ) : (
                <Icon name="send" />
              )}
              {t.send}
            </button>
          ) : (
            <Tone tone="info">{t.ready}</Tone>
          )}
        </div>
      </div>

      {draft.match_reason && <p className="text-xs text-brand mt-2 italic">{draft.match_reason}</p>}

      {error && (
        <p className="text-xs mt-2" style={{ color: 'var(--coral-ink)' }}>
          {error}
        </p>
      )}
    </div>
  );
}
